import { useState, useEffect, useCallback, useRef } from 'react';
import { qa } from '../../services/api';
import { theme } from '../../theme';

const EXEC_STATUS_META = {
  not_run: { label: 'Not Run', color: '#6B7280', bg: '#F3F4F6', icon: '○' },
  pass:    { label: 'Pass',    color: '#16A34A', bg: '#DCFCE7', icon: '✓' },
  fail:    { label: 'Fail',    color: '#DC2626', bg: '#FEE2E2', icon: '✗' },
  blocked: { label: 'Blocked', color: '#D97706', bg: '#FEF3C7', icon: '⛔' },
  retest:  { label: 'Retest',  color: '#7C3AED', bg: '#F5F3FF', icon: '↺' },
};

const PRIORITY_META = {
  high:   { label: 'High',   color: '#DC2626', bg: '#FEF2F2' },
  medium: { label: 'Medium', color: '#D97706', bg: '#FFFBEB' },
  low:    { label: 'Low',    color: '#16A34A', bg: '#F0FDF4' },
};

const inp = { width: '100%', padding: '0.55rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: 6, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', color: theme.text };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' };

function ExecBadge({ v }) {
  const m = EXEC_STATUS_META[v] || EXEC_STATUS_META.not_run;
  return <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>{m.icon} {m.label}</span>;
}

function PriBadge({ v }) {
  const m = PRIORITY_META[v] || { label: v, color: '#6B7280', bg: '#F3F4F6' };
  return <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700 }}>{m.label}</span>;
}

// ── Bug Report Modal ──────────────────────────────────────────────────────────
function ReportBugModal({ exec, onClose, onReported }) {
  const [form, setForm] = useState({
    description: '',
    actual_result: exec.actual_result || '',
    steps_to_reproduce: '',
    severity: 'major',
  });
  const [screenshots, setScreenshots] = useState([]);  // File objects
  const [previews, setPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFiles = e => {
    const files = Array.from(e.target.files);
    setScreenshots(prev => [...prev, ...files]);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = ev => setPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removeShot = i => {
    setScreenshots(prev => prev.filter((_, idx) => idx !== i));
    setPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await qa.reportBug(exec.id, form);
      const bugData = res.data;
      // Upload screenshots if any
      for (const file of screenshots) {
        try { await qa.uploadScreenshot(exec.id, file); } catch {}
      }
      onReported(bugData);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to report bug');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#DC2626' }}>🐛 Report Bug</h2>
            <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>{exec.tc_id} — {exec.title}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {/* Auto-filled context */}
          {(exec.module_name || exec.story_title) && (
            <div style={{ background: '#F8FAFC', border: `1px solid ${theme.border}`, borderRadius: 8, padding: '0.75rem 1rem', display: 'flex', gap: '1.5rem', fontSize: '0.8rem' }}>
              {exec.module_name && <span><strong>Module:</strong> {exec.module_name}</span>}
              {exec.story_title && <span><strong>Story:</strong> {exec.story_title}</span>}
              {exec.module_lead_id && <span style={{ color: '#D97706', fontWeight: 600 }}>→ Will be assigned to Module Lead</span>}
            </div>
          )}

          <div>
            <label style={lbl}>Actual Result *</label>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.actual_result}
              onChange={e => setForm({ ...form, actual_result: e.target.value })} placeholder="What actually happened?" />
          </div>

          <div>
            <label style={lbl}>Steps to Reproduce</label>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.steps_to_reproduce}
              onChange={e => setForm({ ...form, steps_to_reproduce: e.target.value })} placeholder="1. Go to...\n2. Click...\n3. Observe..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={lbl}>Severity</label>
              <select style={inp} value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                <option value="minor">Minor</option>
                <option value="major">Major</option>
                <option value="critical">Critical</option>
                <option value="blocker">Blocker</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Description</label>
              <input style={inp} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description" />
            </div>
          </div>

          {/* Screenshots */}
          <div>
            <label style={lbl}>Screenshots</label>
            <div style={{ border: `2px dashed ${theme.border}`, borderRadius: 8, padding: '0.75rem', cursor: 'pointer', textAlign: 'center', fontSize: '0.8rem', color: theme.textMuted }}
              onClick={() => fileRef.current?.click()}>
              📎 Click to attach screenshots
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />
            </div>
            {previews.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {previews.map((src, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={src} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: `1px solid ${theme.border}` }} />
                    <button onClick={() => removeShot(i)} style={{ position: 'absolute', top: -6, right: -6, background: '#DC2626', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !form.actual_result.trim()}
            style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: 7, background: '#DC2626', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem', opacity: (saving || !form.actual_result.trim()) ? 0.6 : 1 }}>
            {saving ? 'Reporting…' : '🐛 Submit Bug'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Test Case Run Row ─────────────────────────────────────────────────────────
function ExecRow({ exec, onPass, onBugReport }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr style={{ borderBottom: `1px solid ${theme.border}`, background: exec.status === 'retest' ? '#FFF7ED' : 'transparent' }}
        onMouseEnter={e => e.currentTarget.style.background = exec.status === 'retest' ? '#FEF3C7' : '#F8FAFC'}
        onMouseLeave={e => e.currentTarget.style.background = exec.status === 'retest' ? '#FFF7ED' : 'transparent'}>
        <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{exec.tc_id}</td>
        <td style={{ padding: '0.75rem 1rem', fontWeight: 600, maxWidth: 240 }}>
          <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: theme.text, textAlign: 'left', padding: 0 }}>
            {exec.title} {exec.steps?.length > 0 && <span style={{ fontSize: '0.72rem', color: theme.primary }}>{expanded ? '▲' : '▼ steps'}</span>}
          </button>
        </td>
        <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
          {exec.module_name && <span style={{ background: '#EEF2FF', color: theme.primary, padding: '1px 6px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>{exec.module_name}</span>}
        </td>
        <td style={{ padding: '0.75rem 1rem' }}><PriBadge v={exec.priority} /></td>
        <td style={{ padding: '0.75rem 1rem' }}><ExecBadge v={exec.status} /></td>
        <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: theme.textMuted }}>{exec.cycle_name}</td>
        <td style={{ padding: '0.75rem 1rem' }}>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {(exec.status === 'not_run' || exec.status === 'retest') && (
              <>
                <button onClick={() => onPass(exec)} style={{ padding: '0.3rem 0.75rem', border: 'none', borderRadius: 6, background: '#DCFCE7', color: '#16A34A', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>✓ Pass</button>
                <button onClick={() => onBugReport(exec)} style={{ padding: '0.3rem 0.75rem', border: 'none', borderRadius: 6, background: '#FEE2E2', color: '#DC2626', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>🐛 Bug</button>
              </>
            )}
            {exec.status === 'fail' && exec.bug_id && (
              <span style={{ fontSize: '0.75rem', color: '#DC2626', fontWeight: 600 }}>Bug #{exec.bug_id}</span>
            )}
            {exec.status === 'pass' && <span style={{ fontSize: '0.75rem', color: '#16A34A', fontWeight: 600 }}>✓ Done</span>}
          </div>
        </td>
      </tr>
      {expanded && exec.steps?.length > 0 && (
        <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
          <td colSpan={7} style={{ padding: '0 1rem 0.75rem 3rem', background: '#F8FAFC' }}>
            <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden', fontSize: '0.8rem' }}>
              {exec.steps.map((s, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 1fr', gap: '0.5rem', padding: '0.5rem 0.75rem', borderBottom: i < exec.steps.length - 1 ? `1px solid #F3F4F6` : 'none', background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                  <span style={{ fontWeight: 700, color: theme.textMuted }}>{i + 1}</span>
                  <span>{s.step}</span>
                  <span style={{ color: theme.textMuted }}>{s.expected}</span>
                </div>
              ))}
            </div>
            {exec.expected_result && (
              <div style={{ marginTop: '0.5rem', background: '#F0FDF4', border: '1px solid #DCFCE7', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.78rem' }}>
                <strong>Expected:</strong> {exec.expected_result}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main TestManagement Component ─────────────────────────────────────────────
export default function TestManagement() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bugModal, setBugModal] = useState(null);     // exec object
  const [passing, setPassing] = useState(null);       // exec id being passed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await qa.getMyAssignments();
      setAssignments(r.data || []);
    } catch { setAssignments([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const projects = [...new Map(assignments.map(a => [a.project_id, a.project_name])).entries()]
    .map(([id, name]) => ({ id, name }));

  const filtered = assignments.filter(a => {
    if (filterProject !== 'all' && String(a.project_id) !== filterProject) return false;
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    return true;
  });

  const counts = assignments.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});

  const handlePass = async exec => {
    setPassing(exec.id);
    try {
      await qa.updateExecution(exec.id, { status: 'pass' });
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
    finally { setPassing(null); }
  };

  const handleBugReported = () => {
    setBugModal(null);
    load();
  };

  const card = { background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 10, padding: '1.25rem' };
  const sel = { padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, fontSize: '0.875rem', color: theme.text, outline: 'none', background: '#fff' };

  return (
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: theme.text }}>My Test Assignments</h1>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.875rem', color: theme.textMuted }}>Test cases assigned to you across active test cycles</p>
        </div>
        <button onClick={load} style={{ padding: '0.5rem 1rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>↺ Refresh</button>
      </div>

      {/* Summary chips */}
      {assignments.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {Object.entries(EXEC_STATUS_META).map(([k, v]) => counts[k] ? (
            <span key={k} style={{ background: v.bg, color: v.color, padding: '0.3rem 0.75rem', borderRadius: 9999, fontSize: '0.78rem', fontWeight: 700 }}>
              {v.icon} {v.label}: {counts[k]}
            </span>
          ) : null)}
          <span style={{ background: '#F3F4F6', color: theme.textMuted, padding: '0.3rem 0.75rem', borderRadius: 9999, fontSize: '0.78rem', fontWeight: 600 }}>Total: {assignments.length}</span>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <select style={sel} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="all">All Projects</option>
          {projects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
        </select>
        <select style={sel} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          {Object.entries(EXEC_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['TC-ID', 'Title', 'Module', 'Priority', 'Status', 'Cycle', 'Actions'].map(h => (
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 700, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ color: theme.textMuted }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
                    <p style={{ fontWeight: 600, margin: '0 0 0.25rem' }}>No test cases assigned</p>
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>You have no active test assignments right now.</p>
                  </div>
                </td>
              </tr>
            ) : filtered.map(exec => (
              <ExecRow
                key={exec.id}
                exec={exec}
                onPass={handlePass}
                onBugReport={e => setBugModal(e)}
                saving={passing === exec.id}
              />
            ))}
          </tbody>
        </table>
      </div>

      {bugModal && (
        <ReportBugModal
          exec={bugModal}
          onClose={() => setBugModal(null)}
          onReported={handleBugReported}
        />
      )}
    </div>
  );
}
